import { SelectOptionGroup } from '@amzn/meridian/select';
import React, { FC, useState } from 'react';
import { Select, SelectOption, TranslatedString } from '../../../components/blocks/localization';

// The line items table's own select, kept separate from the shared SearchableSelect because it needs
// option groups, matching on a hidden value, an error state and a fixed popover height that adding to
// the shared one would have forced on every other page's dropdown.

export type PoReviewItemOption = {
    value: string;
    label: TranslatedString;
    searchText?: TranslatedString;
};

export type PoReviewItemSelectProps = {
    value?: string;
    onChange(value: string): void;
    options: PoReviewItemOption[];
    // Listed first under their own group. When there are none the options are not grouped.
    pinnedOptions?: PoReviewItemOption[];
    pinnedGroupLabel: TranslatedString;
    otherGroupLabel: TranslatedString;
    placeholder: TranslatedString;
    width: number;
    popoverMaxHeight?: number;
    error?: boolean;
    dataTestId: string;
};

const renderOption = (option: PoReviewItemOption) => (
    <SelectOption key={option.value} value={option.value} label={option.label} />
);

const renderGroup = (label: TranslatedString, options: PoReviewItemOption[]) =>
    options.length > 0 && (
        <SelectOptionGroup key={label} label={label}>
            {options.map(renderOption)}
        </SelectOptionGroup>
    );

const PoReviewItemSelect: FC<PoReviewItemSelectProps> = ({
    value,
    onChange,
    options,
    pinnedOptions = [],
    pinnedGroupLabel,
    otherGroupLabel,
    placeholder,
    width,
    popoverMaxHeight,
    error,
    dataTestId,
}) => {
    const [query, setQuery] = useState('');
    const matches = (option: PoReviewItemOption) =>
        (option.searchText ?? option.label).toLowerCase().includes(query.toLowerCase());
    const visibleOptions = options.filter(matches);
    const isPinned = (option: PoReviewItemOption) => pinnedOptions.some((pinned) => pinned.value === option.value);
    return (
        <Select
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            searchQuery={query}
            onSearch={setQuery}
            width={width}
            popoverMaxHeight={popoverMaxHeight}
            error={error}
            aria-invalid={error ? true : undefined}
            data-testid={dataTestId}
        >
            {pinnedOptions.length > 0
                ? [
                      renderGroup(pinnedGroupLabel, pinnedOptions.filter(matches)),
                      renderGroup(
                          otherGroupLabel,
                          visibleOptions.filter((option) => !isPinned(option))
                      ),
                  ]
                : visibleOptions.map(renderOption)}
        </Select>
    );
};

export { PoReviewItemSelect };
